import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Luxury cart-drawer recommendations enhancement.
 * Deliberately leaves Horizon's native cart-drawer component untouched.
 */
(() => {
  const drawer = document.querySelector('#cart-drawer');
  if (!(drawer instanceof HTMLElement) || drawer.dataset.luxuryRecommendationsReady === 'true') return;

  drawer.dataset.luxuryRecommendationsReady = 'true';

  let activeController = null;
  let loadedProductId = null;

  const getRegion = () => /** @type {HTMLElement | null} */ (drawer.querySelector('[data-cart-recommendations]'));

  const sizedImageUrl = (src, width) => {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  };

  const formatPrice = (cents, region) => {
    try {
      return formatMoney(
        Number(cents) || 0,
        region.dataset.moneyFormat || '{{amount}}',
        region.dataset.currency || 'USD'
      );
    } catch {
      return String(cents ?? '');
    }
  };


  /**
   * Adds a recommended product directly to the cart while keeping the drawer open.
   * Uses Horizon's cart event contract so every cart UI (drawer, count, totals, etc.)
   * hydrates from the same response instead of maintaining a second cart state.
   * @param {HTMLButtonElement} button
   * @param {any} product
   */
  const addRecommendationToCart = async (button, product) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.dataset.loading = 'true';

    // Ask Shopify to return the same cart sections Horizon normally hydrates.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set(['cart-drawer-section']);
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('sections', Array.from(sectionIds).join(','));
    formData.set('sections_url', window.location.pathname);

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    let eventSettled = false;

    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          // Match Horizon's native product-form request.
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (!response.ok || data?.status) {
        const message = data?.description || data?.message || 'تعذر إضافة المنتج إلى السلة';
        eventSettled = true;
        deferredEventPromise.reject(new Error(message));
        button.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: message },
          })
        );
        throw new Error(message);
      }

      // IMPORTANT: do not morph the drawer here. Horizon's cart-items-component
      // owns the morph. Doing it here as well causes a double hydration pass and
      // can bring back the summary/items overlay race.
      const sections = { ...(data.sections || {}) };

      // Some custom /cart/add paths can omit the drawer section. Fetch its fresh
      // HTML WITHOUT morphing it, then let the event perform the one real morph.
      if (!sections['cart-drawer-section']) {
        sections['cart-drawer-section'] = await sectionRenderer.getSectionHTML(
          'cart-drawer-section',
          false,
          new URL(window.location.href)
        );
      }

      // Fetch canonical cart state for counts / quantity sync, exactly like
      // Horizon's native product-form does after a successful add.
      const cartItemsComponent = /** @type {any} */ (document.querySelector('cart-items-component'));
      let ajaxCart;
      if (cartItemsComponent?.fetchCartData) {
        ajaxCart = await cartItemsComponent.fetchCartData();
      } else {
        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error(`Cart refresh failed: ${cartResponse.status}`);
        ajaxCart = await cartResponse.json();
      }

      eventSettled = true;
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          sections,
          items: ajaxCart.items,
          didError: false,
          source: 'luxury-cart-recommendations',
          sourceId: variantId,
          itemCount: 1,
          productId: String(product?.id || ''),
        },
      });

      button.textContent = '✓';
      button.dataset.added = 'true';
    } catch (error) {
      if (!eventSettled) {
        eventSettled = true;
        deferredEventPromise.reject(error);
      }
      console.warn('[luxury-cart-drawer] Quick add failed:', error);
      button.textContent = '!';
      button.dataset.error = 'true';
    } finally {
      window.setTimeout(() => {
        if (!button.isConnected) return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-loading');
        button.removeAttribute('data-added');
        button.removeAttribute('data-error');
        button.textContent = '+';
      }, 900);
    }
  };

  const buildCard = (product, region) => {
    const card = document.createElement('article');
    card.className = 'lux-cart-recommendation';

    const imageLink = document.createElement('a');
    imageLink.className = 'lux-cart-recommendation__image-link';
    imageLink.href = product.url;

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'lux-cart-recommendation__image';
      image.src = sizedImageUrl(product.featured_image, 220);
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.append(image);
    }

    const vendor = document.createElement('p');
    vendor.className = 'lux-cart-recommendation__vendor';
    vendor.textContent = product.vendor || '';

    const title = document.createElement('a');
    title.className = 'lux-cart-recommendation__title';
    title.href = product.url;
    title.textContent = product.title || '';

    const bottom = document.createElement('div');
    bottom.className = 'lux-cart-recommendation__bottom';

    const price = document.createElement('span');
    price.className = 'lux-cart-recommendation__price';
    price.textContent = formatPrice(product.price, region);

    const action = document.createElement('button');
    action.className = 'lux-cart-recommendation__action';
    action.type = 'button';
    action.textContent = '+';

    const availableVariant = Array.isArray(product.variants)
      ? product.variants.find((variant) => variant?.available !== false)
      : null;

    if (availableVariant?.id) {
      action.dataset.variantId = String(availableVariant.id);
      action.setAttribute('aria-label', `أضف ${product.title || 'المنتج'} إلى السلة`);
      action.addEventListener('click', () => addRecommendationToCart(action, product));
    } else {
      action.disabled = true;
      action.setAttribute('aria-label', `${product.title || 'المنتج'} غير متاح حاليًا`);
    }

    bottom.append(price, action);
    card.append(imageLink, vendor, title, bottom);
    return card;
  };

  const loadRecommendations = async (force = false) => {
    const region = getRegion();
    if (!region) return;

    const productId = region.dataset.productId;
    const track = /** @type {HTMLElement | null} */ (region.querySelector('[data-recommendations-track]'));
    if (!productId || !track) return;

    if (!force && loadedProductId === productId && track.children.length > 0) return;

    activeController?.abort();
    activeController = new AbortController();

    const cartProductIds = new Set(
      (region.dataset.cartProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}recommendations/products.json`, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '6');
      url.searchParams.set('intent', 'related');

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: activeController.signal,
      });

      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const { products = [] } = await response.json();
      const productsToShow = products
        .filter((product) => !cartProductIds.has(String(product.id)))
        .slice(0, 3);

      track.replaceChildren();

      if (productsToShow.length === 0) {
        region.hidden = true;
        loadedProductId = productId;
        return;
      }

      const fragment = document.createDocumentFragment();
      productsToShow.forEach((product) => fragment.append(buildCard(product, region)));
      track.append(fragment);
      region.hidden = false;
      loadedProductId = productId;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      region.hidden = true;
      console.warn('[luxury-cart-drawer] Recommendations unavailable:', error);
    }
  };

  drawer.addEventListener(DrawerOpenEvent.eventName, () => loadRecommendations());

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        loadedProductId = null;
        requestAnimationFrame(() => loadRecommendations(true));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[luxury-cart-drawer] Cart update hook failed:', error);
        }
      });
  });

  if (drawer.hasAttribute('open')) loadRecommendations();
})();
